import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatFullName, formatNumber } from 'shared/utils';
import { useProfileLang } from 'shared/hooks/profile.hooks';
import { ROUTES } from 'shared/constants/routes';
import { AG_FILTER_TYPES, textWithFallback } from 'src/components/design-system/table';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import { ReportTable, SET_FILTER, RESETTABLE_FILTER } from './report-table';
import { ReportLinkCell } from './report-link-cell';
import styles from './reports.module.scss';

export function ByPersonReport({ rows, onSelectPerson }) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const { getValueFromLang } = useProfileLang();
  const { formatDayWithWeekday } = useEducationalEventFormat();

  const roleLabel = row =>
    row.isActiveMember ? getValueFromLang(row.role) : lang.REPORT_DEACTIVATED_USER;

  const columns = useMemo(
    () => [
      {
        headerName: lang.REPORT_COL_NAME,
        colId: 'name',
        sortable: true,
        flex: 2,
        cellClass: styles.primaryCell,
        valueGetter: params => (params.data ? formatFullName(params.data) || null : null),
        valueFormatter: textWithFallback,
        cellRenderer: ReportLinkCell,
        cellRendererParams: {
          getTo: data =>
            data?.userId ? ROUTES.CONTACTS_PREVIEW.replace(':id', data.userId) : null,
        },
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
        comparator: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
      },
      {
        headerName: lang.REPORT_COL_ROLE,
        colId: 'role',
        sortable: true,
        flex: 1,
        valueGetter: params => roleLabel(params.data),
        valueFormatter: textWithFallback,
        comparator: (a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()),
        filter: SET_FILTER,
      },
      {
        headerName: lang.REPORT_COL_EVENTS_ATTENDED,
        colId: 'eventsAttended',
        sortable: true,
        flex: 1,
        sort: 'desc',
        valueGetter: params => params.data?.eventsAttended ?? 0,
        cellRenderer: params => formatNumber(params.value ?? 0),
        filter: AG_FILTER_TYPES.NUMBER,
        filterParams: RESETTABLE_FILTER,
      },
      {
        headerName: lang.REPORT_COL_LAST_ATTENDED,
        colId: 'lastAttendedAt',
        sortable: true,
        flex: 1,
        valueGetter: params => params.data?.lastAttendedAt ?? null,
        cellRenderer: params =>
          params.value ? formatDayWithWeekday(params.value) : textWithFallback({ value: null }),
        filter: AG_FILTER_TYPES.DATE,
        filterParams: RESETTABLE_FILTER,
        filterValueGetter: params =>
          params.data?.lastAttendedAt ? new Date(params.data.lastAttendedAt) : null,
        comparator: (a, b) => new Date(a || 0).getTime() - new Date(b || 0).getTime(),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, getValueFromLang, formatDayWithWeekday],
  );

  return (
    <ReportTable
      columnDefs={columns}
      rowData={rows}
      getRowId={params => params.data.userId}
      onRowActivate={data => data?.userId && onSelectPerson(data.userId)}
    />
  );
}
